import React, { useState, useContext, useMemo } from "react";
import "./AgendaUpcomingSidebar.css";
import {
  LuCalendar,
  LuClock,
  LuHourglass,
  LuMapPin,
  LuVideo,
  LuRadio,
  LuChevronRight,
  LuX,
} from "react-icons/lu";
import { Zoom3DIcon } from "./common/Agenda3DIcons";
import AgendaPromoBanner from "./AgendaPromoBanner";
import AgendaEmptyState from "./AgendaEmptyState";
import {
  DEFAULT_SIDEBAR_PROMO,
  getCategoryIcon,
  getCategoryLabel,
} from "../../constants/agendaConstants";
import {
  getSidebarFormattedDate,
  formatShortCountdown,
  getPhysicalLocation,
  getSidebarItemTagInfo,
} from "../../utils/agendaUtils";
import { Language } from "../../App";
import { Spinner, Modal } from "reactstrap";
import defaultAgenda1 from "../../assets/images/default_agenda_1.jpg";
import CreateAgendaCard from "./CreateAgendaCard";

function AgendaThumbImage({ src, alt, defaultSrc }) {
  const [loaded, setLoaded] = useState(false);

  return (
    <div
      className="agenda-timeline-thumb-wrap d-flex align-items-center justify-content-center"
      style={{
        position: "relative",
        background: "#F4F7FB",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          opacity: loaded ? 0 : 1,
          transition: "opacity 0.35s ease-out",
          pointerEvents: "none",
          zIndex: 1,
        }}
      >
        <Spinner
          style={{
            color: "#1dc1dd",
            width: "5px",
            height: "5px",
            borderWidth: "2px",
          }}
        />
      </div>
      <img
        src={src || defaultSrc}
        alt={alt || "Agenda"}
        className="agenda-timeline-thumb-img"
        onLoad={() => setLoaded(true)}
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = defaultSrc;
          setLoaded(true);
        }}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          opacity: loaded ? 1 : 0,
          transition: "opacity 0.45s cubic-bezier(0.16, 1, 0.3, 1), transform 0.35s cubic-bezier(0.16, 1, 0.3, 1)",
          willChange: "opacity, transform",
        }}
      />
    </div>
  );
}

export function AgendaUpcomingSidebarItemShimmer({
  hasCountdown = false,
  titleWidth = "58%",
  timeWidth = "45%",
  locWidth = "30%",
  typeWidth = "30%",
}) {
  return (
    <div className="agenda-sidebar-shimmer-row" aria-hidden="true">
      {/* Thumbnail */}
      <div className="agenda-sidebar-shimmer-thumb agenda-shimmer-block" />

      {/* Info Column - 3 Lines */}
      <div className="agenda-sidebar-shimmer-info">
        {/* Line 1: Title + (optional Countdown) */}
        <div className="d-flex align-items-center justify-content-between gap-2 w-100">
          <span
            className="agenda-sidebar-shimmer-title agenda-shimmer-block"
            style={{ width: titleWidth }}
          />
          {hasCountdown && (
            <span
              className="agenda-sidebar-shimmer-countdown agenda-shimmer-block"
              style={{ width: "36px", height: "18px", borderRadius: "5px", flexShrink: 0 }}
            />
          )}
        </div>

        {/* Line 2: Time */}
        <div className="d-flex align-items-center justify-content-between gap-2 w-100">
          <span
            className="agenda-sidebar-shimmer-meta agenda-shimmer-block"
            style={{ width: timeWidth }}
          />
        </div>

        {/* Line 3: Mode Tag (Online/Offline/Hybrid) + Category Badge */}
        <div className="d-flex align-items-center justify-content-between gap-2 w-100">
          <span
            className="agenda-sidebar-shimmer-badge agenda-shimmer-block"
            style={{ width: locWidth, height: "18px", borderRadius: "5px", flexShrink: 0 }}
          />
          <span
            className="agenda-sidebar-shimmer-badge agenda-shimmer-block"
            style={{ width: typeWidth, height: "18px", borderRadius: "5px", flexShrink: 0 }}
          />
        </div>
      </div>
    </div>
  );
}

export function AgendaUpcomingSidebarShimmer({ isSuggested = false }) {
  const shimmerLabel = isSuggested ? "Loading suggested events" : "Loading upcoming agendas";
  return (
    <div
      className="agenda-sidebar-shimmer-container"
      role="status"
      aria-label={shimmerLabel}
    >
      {/* Group 1 */}
      <div className="agenda-sidebar-shimmer-group">
        <span
          className="agenda-sidebar-shimmer-group-label agenda-shimmer-block"
          style={{ width: "115px" }}
        />
        <div className="agenda-timeline-items">
          <AgendaUpcomingSidebarItemShimmer
            hasCountdown={true}
            titleWidth="75%"
            timeWidth="45%"
            locWidth="30%"
          />
          <AgendaUpcomingSidebarItemShimmer
            hasCountdown={false}
            titleWidth="68%"
            timeWidth="40%"
            locWidth="28%"
          />
        </div>
      </div>

      {/* Group 2 */}
      <div className="agenda-sidebar-shimmer-group">
        <span
          className="agenda-sidebar-shimmer-group-label agenda-shimmer-block"
          style={{ width: "140px" }}
        />
        <div className="agenda-timeline-items">
          <AgendaUpcomingSidebarItemShimmer
            hasCountdown={true}
            titleWidth="80%"
            timeWidth="48%"
            locWidth="32%"
          />
          <AgendaUpcomingSidebarItemShimmer
            hasCountdown={false}
            titleWidth="72%"
            timeWidth="42%"
            locWidth="28%"
          />
        </div>
      </div>
      <span className="visually-hidden">{shimmerLabel}</span>
    </div>
  );
}

/**
 * Reusable Agenda Item Row (Used in both Sidebar and "See All" Popup Modal)
 */
export function AgendaSidebarItemRow({ item, groupKey, onSelectEvent, lang }) {
  const physicalLocation = getPhysicalLocation(item);
  const tagInfo = getSidebarItemTagInfo(item, groupKey, lang);

  const handleClick = () => {
    const rawId =
      item.cleanId ||
      item.originalId ||
      item.apiId ||
      item.raw_id ||
      item.raw?.id ||
      item.id ||
      "";
    const cleanId = rawId
      ? String(rawId)
          .replace(/_occ_.*$/, "")
          .replace(/^(webinar-|session-|evt-|event-|mentorship-)/i, "")
      : "";
    onSelectEvent?.({
      ...item,
      cleanId: cleanId || item.id,
      originalId: cleanId || item.originalId || item.id,
    });
  };

  return (
    <div
      className="agenda-timeline-row"
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      {/* Thumbnail Wrap with Shimmer Loading */}
      <AgendaThumbImage
        src={item.image}
        alt={item.title}
        defaultSrc={defaultAgenda1}
      />

      {/* Main Content Area: Exactly 3 balanced lines */}
      <div className="agenda-timeline-content">
        {/* Line 1: Agenda Title & (Countdown/Date Badge opposite of title) */}
        <div className="agenda-timeline-title-row">
          <h6 className="agenda-timeline-title" title={item.title}>
            {item.title}
          </h6>

          {tagInfo && (
            <div className="agenda-timeline-tags-group">
              <span
                className={`agenda-pill-countdown ${
                  tagInfo.isLive ? "is-live" : ""
                }`}
                title={tagInfo.title}
              >
                {tagInfo.isLive ? (
                  <LuRadio size={10} />
                ) : tagInfo.type === "date" ? (
                  <LuCalendar size={10} />
                ) : (
                  <LuHourglass size={10} />
                )}
                <span>{tagInfo.label}</span>
              </span>
            </div>
          )}
        </div>

        {/* Line 2: Time / Date */}
        <div className="agenda-timeline-time-row">
          <span className="agenda-meta-time">
            {item.endDate && item.endDate !== item.date ? (
              <LuCalendar size={11} className="agenda-meta-icon" />
            ) : (
              <LuClock size={11} className="agenda-meta-icon" />
            )}
            <span>
              {getSidebarFormattedDate(
                item.date,
                item.endDate,
                item.time
              )}
            </span>
          </span>
        </div>

        {/* Line 3: Mode Tag (Online / Offline / Hybrid), Physical Location & Category Pill */}
        <div className="agenda-timeline-host-row">
          <div className="agenda-timeline-badges-left">
            {item.isHybrid ? (
              <span
                className="agenda-location-badge is-hybrid"
                title={lang?.hybrid || "Hybrid"}
              >
                <LuRadio size={10} />
                <span>{lang?.hybrid || "Hybrid"}</span>
              </span>
            ) : item.isOnline ? (
              <>
                <span
                  className="agenda-location-badge is-online"
                  title={lang?.online || "Online"}
                >
                  <LuVideo size={10} />
                  <span>{lang?.online || "Online"}</span>
                </span>
                {item.type !== "event" && <Zoom3DIcon height={18} />}
              </>
            ) : (
              <span
                className="agenda-location-badge is-location"
                title={lang?.offline || "Offline"}
              >
                <LuMapPin size={10} />
                <span>{lang?.offline || "Offline"}</span>
              </span>
            )}

            {physicalLocation ? (
              <span
                className="agenda-location-badge is-location agenda-badge-venue"
                title={item.location || physicalLocation}
              >
                <LuMapPin size={10} />
                <span>{physicalLocation}</span>
              </span>
            ) : null}
          </div>

          <span className={`agenda-pill-type pill-${item.type}`}>
            {getCategoryIcon(item.type, 10)}
            <span>{getCategoryLabel(item.type, false, true, lang)}</span>
          </span>
        </div>
      </div>
    </div>
  );
}

export default function AgendaUpcomingSidebar({
  createCard,
  groupedSchedules,
  onSelectEvent,
  onCreateEvent,
  isLoading,
  selectedCategory = "all",
  ads = [],
  isAdsLoading = false,
  isSuggested = false,
  title,
}) {
  const { lang } = useContext(Language) || {};
  const [isAllModalOpen, setIsAllModalOpen] = useState(false);
  const MAX_SIDEBAR_ITEMS = 5;

  const hasSchedules = groupedSchedules?.length > 0;
  const showEmptyState = !isLoading && !hasSchedules;

  const totalItemsCount = useMemo(
    () =>
      groupedSchedules?.reduce(
        (acc, group) => acc + (group.items?.length || 0),
        0
      ) || 0,
    [groupedSchedules]
  );

  // Slice grouped schedules to show at most 5 items in the sidebar
  const visibleGroupedSchedules = useMemo(() => {
    if (!groupedSchedules || groupedSchedules.length === 0) return [];

    let count = 0;
    const result = [];

    for (const group of groupedSchedules) {
      if (count >= MAX_SIDEBAR_ITEMS) break;
      const remaining = MAX_SIDEBAR_ITEMS - count;
      const slicedItems = (group.items || []).slice(0, remaining);
      if (slicedItems.length > 0) {
        result.push({
          ...group,
          items: slicedItems,
        });
        count += slicedItems.length;
      }
    }

    return result;
  }, [groupedSchedules]);

  const sidebarTitle =
    title ||
    (isSuggested
      ? lang?.suggested_events || "Suggested Events"
      : lang?.upcoming_agendas || "Upcoming Agendas");

  return (
    <aside className="agenda-sidebar-left" aria-labelledby="agenda-sidebar-heading">
      {/* Create Agenda CTA Card (Matching Courses CreateCourseCard) */}
      {createCard || (onCreateEvent ? <CreateAgendaCard onClick={onCreateEvent} /> : null)}

      {/* Unified Schedules Panel Card */}
      <div
        className={`agenda-sidebar-card ${
          showEmptyState ? "agenda-sidebar-card-empty" : ""
        }`}
      >
        {/* Ambient Glow */}
        <div className="agenda-sidebar-ambient-glow" aria-hidden="true" />

        {/* Header */}
        <div className="agenda-sidebar-header">
          <div className="d-flex align-items-center gap-2">
            <div className="agenda-sidebar-header-icon" aria-hidden="true">
              <LuCalendar size={15} />
            </div>
            <h2 id="agenda-sidebar-heading" className="agenda-sidebar-title">{sidebarTitle}</h2>
          </div>
          {!isLoading && totalItemsCount > 0 && (
            <span className="agenda-header-count-pill">{totalItemsCount}</span>
          )}
        </div>

        {/* Scrollable timeline list area */}
        <div
          className={`agenda-sidebar-body ${
            showEmptyState ? "agenda-sidebar-body-empty" : ""
          }`}
        >
          {isLoading ? (
            <AgendaUpcomingSidebarShimmer isSuggested={isSuggested} />
          ) : hasSchedules ? (
            <>
              {visibleGroupedSchedules.map((group) => (
                <div key={group.groupKey} className="agenda-timeline-group">
                  {/* Date Sub-Header */}
                  <div className="agenda-timeline-date-header">
                    <div className="agenda-timeline-date-node" />
                    <span className="agenda-timeline-date-title">{group.title}</span>
                    <div className="agenda-timeline-date-line" />
                  </div>

                  {/* Group Schedule Item Rows (Limited to 5 total in sidebar) */}
                  <div className="agenda-timeline-items">
                    {group.items.map((item) => (
                      <AgendaSidebarItemRow
                        key={item.id}
                        item={item}
                        groupKey={group.groupKey}
                        onSelectEvent={onSelectEvent}
                        lang={lang}
                      />
                    ))}
                  </div>
                </div>
              ))}

              {/* See All Button at bottom if total items > 5 */}
              {!isLoading && totalItemsCount > MAX_SIDEBAR_ITEMS && (
                <div className="agenda-sidebar-see-all-wrapper">
                  <button
                    type="button"
                    className="agenda-sidebar-see-all-btn"
                    onClick={() => setIsAllModalOpen(true)}
                    aria-label={`${lang?.see_all_text || lang?.see_all || "See All"} (${totalItemsCount})`}
                  >
                    <span>
                      {lang?.see_all_text || lang?.see_all || "See All"} ({totalItemsCount})
                    </span>
                    <LuChevronRight size={15} className="agenda-sidebar-see-all-arrow" />
                  </button>
                </div>
              )}
            </>
          ) : (
            <AgendaEmptyState
              variant="sidebar"
              category={selectedCategory}
              onAction={onCreateEvent}
            />
          )}
        </div>
      </div>

      {/* Sidebar Ad / Promo Banner Card */}
      <AgendaPromoBanner
        ads={ads}
        loading={isAdsLoading}
        promo={DEFAULT_SIDEBAR_PROMO}
      />

      {/* Modern Web Guidance Modal: See All Upcoming Agendas */}
      <Modal
        isOpen={isAllModalOpen}
        toggle={() => setIsAllModalOpen((prev) => !prev)}
        centered
        className="agenda-all-modal-dialog"
        contentClassName="agenda-all-modal-content"
        backdropClassName="agenda-all-modal-backdrop"
        fade
        role="dialog"
        aria-modal="true"
        aria-labelledby="agenda-all-modal-title"
      >
        <div className="agenda-all-modal-card" role="document">
          {/* Ambient Glow matching modern web guidance & theme */}
          <div className="agenda-all-modal-ambient-glow" aria-hidden="true" />

          {/* Accessible Close Button */}
          <button
            type="button"
            className="agenda-all-modal-close-btn"
            onClick={() => setIsAllModalOpen(false)}
            aria-label={lang?.close || "Close"}
          >
            <LuX size={16} />
          </button>

          {/* Modal Header */}
          <div className="agenda-all-modal-header">
            <div className="d-flex align-items-center gap-2">
              <div className="agenda-sidebar-header-icon" aria-hidden="true">
                <LuCalendar size={15} />
              </div>
              <h2 id="agenda-all-modal-title" className="agenda-all-modal-title">
                {sidebarTitle}
              </h2>
              {totalItemsCount > 0 && (
                <span className="agenda-header-count-pill">{totalItemsCount}</span>
              )}
            </div>
            <p className="agenda-all-modal-subtitle">
              {isSuggested
                ? (lang?.suggested_events_desc || "Explore suggested events tailored for your medical specialties")
                : (lang?.upcoming_agendas_desc || "All scheduled webinars, clinical sessions, events and mentorships")}
            </p>
          </div>

          {/* Scrollable Modal Body containing all items */}
          <div className="agenda-all-modal-body">
            {groupedSchedules.map((group) => (
              <div key={group.groupKey} className="agenda-timeline-group">
                {/* Date Sub-Header */}
                <div className="agenda-timeline-date-header">
                  <div className="agenda-timeline-date-node" />
                  <span className="agenda-timeline-date-title">{group.title}</span>
                  <div className="agenda-timeline-date-line" />
                </div>

                {/* Group Schedule Item Rows */}
                <div className="agenda-timeline-items">
                  {group.items.map((item) => (
                    <AgendaSidebarItemRow
                      key={item.id}
                      item={item}
                      groupKey={group.groupKey}
                      onSelectEvent={(selected) => {
                        setIsAllModalOpen(false);
                        onSelectEvent(selected);
                      }}
                      lang={lang}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Modal>
    </aside>
  );
}

